// Sugalarm — a variant-aware local wall-clock time control (cycle 330).
//
// The kiosk has no OS keyboard, so time entry there is a keyboard-free
// ‹ › stepper; mobile/web use a native <input type="time">. This is the
// same shape the cycle-145 alarm-clock card uses.
//
// It is deliberately a SEPARATE, self-contained component rather than a
// refactor of `AlarmClockModeSection`: the bedside alarm-clock schedule is
// a pre-existing feature this cycle is explicitly not allowed to touch or
// merge with, so its copy of these helpers is left byte-identical. Any
// future consolidation should move BOTH onto this file.
//
// Values are local wall-clock "HH:MM" (never UTC) — the `alarm_clock` /
// `tts_quiet_hours_*` convention.

import type { FrameVariant } from "../../hooks/useFrameVariant";
import styles from "./SettingsScreen.module.css";

/** "22:00" → "10:00 PM" (or "22:00" on a 24-hour clock). */
export function fmtTimeLabel(hhmm: string, h24: boolean): string {
  const [h, m] = hhmm.split(":").map(Number);
  if (h == null || m == null || Number.isNaN(h) || Number.isNaN(m)) {
    return hhmm;
  }
  if (h24) return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
}

/** Shift "HH:MM" by `minutes`, wrapping around midnight. */
function shiftTime(hhmm: string, minutes: number): string {
  const [h, m] = hhmm.split(":").map(Number);
  if (h == null || m == null || Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const total = (((h * 60 + m + minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(
    total % 60,
  ).padStart(2, "0")}`;
}

export function TimeField({
  value,
  h24,
  variant,
  ariaLabel,
  onChange,
}: {
  value: string;
  h24: boolean;
  variant: FrameVariant;
  ariaLabel: string;
  onChange: (next: string) => void;
}) {
  if (variant === "kiosk") {
    const btn = (label: string, delta: number, aria: string) => (
      <button
        type="button"
        className={styles.btnSecondary}
        style={{ minWidth: 44, minHeight: 44, padding: "6px 10px" }}
        aria-label={`${ariaLabel}: ${aria}`}
        onClick={() => onChange(shiftTime(value, delta))}
      >
        {label}
      </button>
    );
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        {btn("−1h", -60, "one hour earlier")}
        {btn("−15m", -15, "fifteen minutes earlier")}
        <span
          className="tnum"
          style={{ minWidth: 86, textAlign: "center", fontWeight: 700 }}
        >
          {fmtTimeLabel(value, h24)}
        </span>
        {btn("+15m", 15, "fifteen minutes later")}
        {btn("+1h", 60, "one hour later")}
      </span>
    );
  }

  return (
    <input
      type="time"
      value={value}
      aria-label={ariaLabel}
      onChange={(e) => {
        // An empty value means the picker is mid-edit — don't PATCH a
        // blank (the server would 422 it).
        if (e.target.value) onChange(e.target.value);
      }}
      style={{
        background: "var(--bg-sunk)",
        border: "0.5px solid var(--line-strong)",
        borderRadius: 8,
        color: "var(--ink)",
        padding: "8px 10px",
        fontFamily: "inherit",
        minHeight: 44,
      }}
    />
  );
}
